import { type UseQueryResult, useQueries } from "@tanstack/react-query";
import { Loader2, UserX, X } from "lucide-react";
import type React from "react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomAvatar } from "@/components/common/CustomAvatar";
import { BlockUserConfirmDialog } from "@/components/ui/BlockUserConfirmDialog";
import { Button } from "@/components/ui/button";
import { useBlockedUsersList } from "@/hooks/useBlockedUsersList";
import { useBlockUser, userQueryKeys } from "@/hooks/useBlockUser";
import { useUnblockUser } from "@/hooks/useUnblockUser";
import type { User } from "@/lib/humhub-api/types";
import { usersApi } from "@/lib/humhub-api/users";

interface BlockedUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const BlockedUsersModal: React.FC<BlockedUsersModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();

  const { blockUser, isBlocking } = useBlockUser();
  const { unblockUser, isUnblocking } = useUnblockUser();
  const isHandlingActionRef = useRef(false);

  const [unblockDialogState, setUnblockDialogState] = useState<{
    isOpen: boolean;
    user: { id: number; name: string; avatarUrl: string | null } | null;
  }>({ isOpen: false, user: null });

  const {
    data: blockedUserIds = [],
    isLoading,
    isError,
    refetch,
  } = useBlockedUsersList({ enabled: isOpen });

  const userQueries = useQueries({
    queries: blockedUserIds.map((id) => {
      const numericId = Number.parseInt(id, 10);
      return {
        queryKey: userQueryKeys.byId(numericId),
        queryFn: () => usersApi.getUserById(numericId),
        enabled: isOpen && Number.isInteger(numericId),
        staleTime: 1000 * 60 * 5,
      };
    }),
  }) as UseQueryResult<User, Error>[];

  const blockedUsers = useMemo(() => {
    return blockedUserIds.map((id, index) => {
      const query = userQueries[index];
      const user = query?.data as User | undefined;
      return {
        id,
        displayName: user?.display_name ?? null,
        imageUrl: user?.image_url ?? null,
        isLoading: query?.isLoading ?? false,
        isError: query?.isError ?? false,
      };
    });
  }, [blockedUserIds, userQueries]);

  const handleRefresh = () => {
    void refetch();
    userQueries.forEach((query) => {
      void query.refetch();
    });
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="mx-4 flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg bg-white">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b p-6">
          <div className="flex items-center gap-2">
            <UserX className="h-5 w-5" />
            <h2 className="font-semibold text-xl">{t("common.blockedUsers")}</h2>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            <div className="space-y-4 p-6">
              <p className="text-gray-600 text-sm">{t("common.blockedUsersDescription")}</p>

              {isLoading ? (
                <div className="flex items-center justify-center gap-2 py-10 text-gray-500">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>{t("common.loading")}</span>
                </div>
              ) : isError ? (
                <div className="flex flex-col items-center gap-4 rounded-lg border border-red-200 bg-red-50 p-6 text-center">
                  <p className="text-red-700 text-sm">{t("common.blockedUsersError")}</p>
                  <Button variant="outline" size="sm" onClick={handleRefresh}>
                    {t("common.retry")}
                  </Button>
                </div>
              ) : blockedUsers.length === 0 ? (
                <div className="rounded-lg border border-gray-200 border-dashed bg-gray-50 p-6 text-center text-gray-500 text-sm">
                  {t("common.blockedUsersEmpty")}
                </div>
              ) : (
                <ul className="space-y-3">
                  {blockedUsers.map((blockedUser) => (
                    <li
                      key={blockedUser.id}
                      className="flex items-center gap-3 rounded-lg border border-gray-200 p-4"
                    >
                      {blockedUser.displayName ? (
                        <CustomAvatar
                          name={blockedUser.displayName}
                          imageUrl={blockedUser.imageUrl ?? undefined}
                          size={40}
                          roundedFull
                        />
                      ) : (
                        <div className="flex size-10 items-center justify-center rounded-full bg-gray-100 text-gray-500">
                          <UserX className="h-5 w-5" />
                        </div>
                      )}

                      <div className="flex flex-1 flex-col">
                        <span className="font-medium text-gray-900">
                          {blockedUser.isLoading
                            ? t("common.loading")
                            : (blockedUser.displayName ?? t("common.user"))}
                        </span>
                      </div>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setUnblockDialogState({
                            isOpen: true,
                            user: {
                              id: Number(blockedUser.id),
                              name: blockedUser.displayName || t("common.user"),
                              avatarUrl: blockedUser.imageUrl,
                            },
                          })
                        }
                        disabled={blockedUser.isLoading || blockedUser.isError}
                      >
                        {t("common.unblock")}
                      </Button>

                      {blockedUser.isLoading && (
                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                      )}
                      {blockedUser.isError && !blockedUser.isLoading && (
                        <span className="text-red-500 text-xs">
                          {t("common.blockedUsersPartialError")}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 border-t bg-gray-50 p-6">
          <Button onClick={onClose}>{t("common.close")}</Button>
        </div>
      </div>

      {unblockDialogState.user && (
        <BlockUserConfirmDialog
          isOpen={unblockDialogState.isOpen}
          onClose={() => setUnblockDialogState({ isOpen: false, user: null })}
          dialogTitle={t("common.confirmUnblockTitle", "Unblock User")}
          dialogDescription={t(
            "common.confirmUnblockDescription",
            "Are you sure you want to unblock this user?",
          )}
          isBlocked={true}
          isBlocking={isBlocking}
          isUnblocking={isUnblocking}
          user={unblockDialogState.user}
          blockUser={blockUser}
          unblockUser={unblockUser}
          t={t}
          isHandlingActionRef={isHandlingActionRef}
        />
      )}
    </div>
  );
};
